import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Save, AlertCircle, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { adminApi, default as api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { SecurityMfaCard } from './settings/SecurityMfaCard';
import { BrandingCard } from './settings/BrandingCard';
import { EmailSettingsCard } from './settings/EmailSettingsCard';
import { CategoriesCard } from './settings/CategoriesCard';
import { LegalDeadlinesCard } from './settings/LegalDeadlinesCard';
import { PolicyFooterCard } from './settings/PolicyFooterCard';
import { PushNotificationsCard } from './settings/PushNotificationsCard';

const SettingsPage = () => {
  const { user, tenant } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingBranding, setSavingBranding] = useState(false);
  const [uploadingHero, setUploadingHero] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [detectingColor, setDetectingColor] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);
  const [newCategory, setNewCategory] = useState('');
  const heroFileRef = useRef(null);
  const logoFileRef = useRef(null);

  const [formData, setFormData] = useState({
    sendgrid_api_key: '',
    sender_email: '',
    categories: [],
    acknowledgment_days: 7,
    feedback_days: 90,
    retention_years: 5,
    // Rodapé — políticas & documentação
    privacy_policy_content: '',
    privacy_policy_url: '',
    terms_of_use_content: '',
    terms_of_use_url: '',
    code_of_ethics_url: '',
    code_of_ethics_pdf_url: '',
    pprcic_url: '',
    pprcic_pdf_url: '',
    // Web Push per-tenant recipient config (v64)
    push_enabled: true,
    push_notify_roles: ['company_admin', 'compliance_officer', 'triage_manager', 'dpo'],
    push_notify_events: ['NEW_REPORT', 'ASSIGNMENT', 'CONFLICT_ALERT', 'DEADLINE_ALERT', 'CRITICAL'],
    push_digest_enabled: true,
  });

  const [brandingData, setBrandingData] = useState({
    hero_image_url: '',
    hero_title: 'Canal de Denúncias Interno',
    hero_subtitle: 'Submeta a sua denúncia de forma segura e confidencial',
    primary_color: '#0F172A',
    company_logo_url: '',
    website_url: '',
    layout_style: 'classic',
    institutional_text: '',
    alternative_channels_text: '',
    support_email: '',
    support_signature: ''
  });

  const fetchSettings = useCallback(async () => {
    try {
      const response = await adminApi.getSettings();
      setSettings(response.data);
      setFormData({
        sendgrid_api_key: '',
        sender_email: response.data.sender_email || '',
        categories: response.data.categories || [],
        acknowledgment_days: response.data.acknowledgment_days || 7,
        feedback_days: response.data.feedback_days || 90,
        retention_years: response.data.retention_years || 5,
        privacy_policy_content: response.data.privacy_policy_content || '',
        privacy_policy_url: response.data.privacy_policy_url || '',
        terms_of_use_content: response.data.terms_of_use_content || '',
        terms_of_use_url: response.data.terms_of_use_url || '',
        code_of_ethics_url: response.data.code_of_ethics_url || '',
        code_of_ethics_pdf_url: response.data.code_of_ethics_pdf_url || '',
        pprcic_url: response.data.pprcic_url || '',
        pprcic_pdf_url: response.data.pprcic_pdf_url || '',
        push_enabled: response.data.push_enabled !== false,
        push_notify_roles: Array.isArray(response.data.push_notify_roles)
          ? response.data.push_notify_roles
          : ['company_admin', 'compliance_officer', 'triage_manager', 'dpo'],
        push_notify_events: Array.isArray(response.data.push_notify_events)
          ? response.data.push_notify_events
          : ['NEW_REPORT', 'ASSIGNMENT', 'CONFLICT_ALERT', 'DEADLINE_ALERT', 'CRITICAL'],
        push_digest_enabled: response.data.push_digest_enabled !== false,
      });
      setBrandingData({
        hero_image_url: response.data.hero_image_url || '',
        hero_title: response.data.hero_title || 'Canal de Denúncias Interno',
        hero_subtitle: response.data.hero_subtitle || 'Submeta a sua denúncia de forma segura e confidencial',
        primary_color: response.data.primary_color || '#0F172A',
        company_logo_url: response.data.company_logo_url || '',
        website_url: response.data.website_url || '',
        layout_style: response.data.layout_style || 'classic',
        institutional_text: response.data.institutional_text || '',
        alternative_channels_text: response.data.alternative_channels_text || '',
        support_email: response.data.support_email || '',
        support_signature: response.data.support_signature || ''
      });
    } catch (err) {
      setError('Erro ao carregar definições');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleRestoreTemplate = async (kind) => {
    setError(''); setSuccess('');
    try {
      const { data } = await api.post(`/admin/settings/restore-policy-template/${kind}`);
      setFormData(prev => ({
        ...prev,
        ...(kind === 'privacy'
          ? { privacy_policy_content: data.content, privacy_policy_url: data.url }
          : { terms_of_use_content: data.content, terms_of_use_url: data.url }),
      }));
      setSuccess(`Template global de ${kind === 'privacy' ? 'privacidade' : 'termos'} restaurado. Não esqueça de guardar.`);
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao restaurar template global');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const updateData = { ...formData };
      if (!updateData.sendgrid_api_key) {
        delete updateData.sendgrid_api_key;
      }

      await adminApi.updateSettings(updateData);
      setSuccess('Definições guardadas com sucesso');
      setTimeout(() => setSuccess(''), 3000);
      await fetchSettings();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao guardar definições');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveBranding = async () => {
    setSavingBranding(true);
    setError('');
    setSuccess('');

    try {
      await adminApi.updateBrandingSettings(brandingData);
      setSuccess('Definições de branding guardadas com sucesso');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao guardar branding');
    } finally {
      setSavingBranding(false);
    }
  };

  const handleHeroImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Por favor, selecione uma imagem válida');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError('A imagem deve ter no máximo 5MB');
      return;
    }

    setUploadingHero(true);
    setError('');

    try {
      const response = await adminApi.uploadHeroImage(file);
      setBrandingData(prev => ({ ...prev, hero_image_url: response.data.hero_image_url }));
      setSuccess('Imagem carregada com sucesso');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao carregar imagem');
    } finally {
      setUploadingHero(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Por favor, selecione uma imagem válida');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setError('O logo deve ter no máximo 2MB');
      return;
    }

    setUploadingLogo(true);
    setError('');

    try {
      const response = await adminApi.uploadLogo(file);
      setBrandingData(prev => ({ ...prev, company_logo_url: response.data.url }));
      setSuccess('Logo carregado com sucesso');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao carregar logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleDetectColor = async () => {
    if (!brandingData.website_url) {
      setError('Indique o URL do site primeiro');
      return;
    }
    setDetectingColor(true);
    setError('');
    try {
      const response = await adminApi.detectBrandColor(brandingData.website_url);
      setBrandingData(prev => ({ ...prev, primary_color: response.data.color }));
      setSuccess(`Cor detetada (${response.data.source === 'theme-color' ? 'meta tag do site' : 'favicon'}): ${response.data.color}`);
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Não foi possível detetar uma cor no site indicado');
    } finally {
      setDetectingColor(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-slate-400">A carregar...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>
          Definições
        </h1>
        <p className="text-slate-500 mt-1">Configure as definições do canal de denúncias</p>
      </div>

      {error && (
        <Alert className="bg-red-50 border-red-200">
          <AlertCircle className="w-4 h-4 text-red-600" />
          <AlertDescription className="text-red-700">{error}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="bg-emerald-50 border-emerald-200">
          <CheckCircle className="w-4 h-4 text-emerald-600" />
          <AlertDescription className="text-emerald-700">{success}</AlertDescription>
        </Alert>
      )}

      <SecurityMfaCard user={user} />

      <BrandingCard
        brandingData={brandingData}
        setBrandingData={setBrandingData}
        tenant={tenant}
        heroFileRef={heroFileRef}
        uploadingHero={uploadingHero}
        onHeroImageUpload={handleHeroImageUpload}
        logoFileRef={logoFileRef}
        uploadingLogo={uploadingLogo}
        onLogoUpload={handleLogoUpload}
        detectingColor={detectingColor}
        onDetectColor={handleDetectColor}
        savingBranding={savingBranding}
        onSaveBranding={handleSaveBranding}
      />

      {settings?.use_global_email !== false ? (
        <div
          className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600 flex items-start gap-3"
          data-testid="email-global-notice"
        >
          <span className="mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-indigo-100 text-indigo-700 text-xs font-semibold">i</span>
          <p>
            Este canal está a usar o <strong>email global da plataforma CogniEtica</strong>.
            A configuração de SendGrid dedicada foi ocultada. Para usar um remetente próprio,
            peça ao Super Admin para desativar o email global nas definições da plataforma.
          </p>
        </div>
      ) : (
        <EmailSettingsCard
          formData={formData}
          setFormData={setFormData}
          settings={settings}
          showApiKey={showApiKey}
          setShowApiKey={setShowApiKey}
        />
      )}

      <CategoriesCard
        formData={formData}
        setFormData={setFormData}
        newCategory={newCategory}
        setNewCategory={setNewCategory}
      />

      <LegalDeadlinesCard formData={formData} setFormData={setFormData} />

      <PolicyFooterCard
        formData={formData}
        setFormData={setFormData}
        onRestoreTemplate={handleRestoreTemplate}
        onFetchSettings={fetchSettings}
      />

      <PushNotificationsCard formData={formData} setFormData={setFormData} />

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving} data-testid="save-settings-btn">
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'A guardar...' : 'Guardar Definições'}
        </Button>
      </div>
    </div>
  );
};

export default SettingsPage;
